import React, {useEffect} from 'react'
import {Box, Button, Flex, Grid, Link, Stack, Text, useMultiStyleConfig} from '@chakra-ui/react'
import {defineMessage, useIntl, FormattedMessage} from 'react-intl'
import ProductRegistrationHeader from './partials/product-registration-header'
import ProductRegistrationStepper from './partials/product-registration-stepper'
import ProductRegistrationProducts from './partials/product-registration-products'
import useNavigation from '@salesforce/retail-react-app/app/hooks/use-navigation'
import {useProductSearch} from '@salesforce/commerce-sdk-react'
import {useLocation} from 'react-router-dom'
import routingObject from '../../utils/static-routes'
import {useHistory} from 'react-router-dom/cjs/react-router-dom.min'
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site'
import {useCurrency} from '@salesforce/retail-react-app/app/hooks'
import LoadingSpinner from '../../components/loading-spinner'
import {useGoogleTagManager} from '../../custom-api/hooks/use-google-tag-manager'
import {registration_event} from '../../custom-api/layers/product/registration'
import {getConfig} from '@salesforce/pwa-kit-runtime/utils/ssr-config'
import {Helmet} from 'react-helmet'

const ProductRegistrationSelect = () => {
    const styles = useMultiStyleConfig('ProductRegistration', {variant: 'productSearch'})

    const location = useLocation()
    const navigate = useNavigation()
    const history = useHistory()
    const {site, locale} = useMultiSite()
    const {currency} = useCurrency()

    const queryParams = new URLSearchParams(location.search)
    const textParam = queryParams.get('text')
    const snParam = queryParams.get('sn')

    // Determine which query to use for the search
    const getSearchQuery = () => {
        if (snParam) return snParam
        return textParam
    }

    const searchQuery = getSearchQuery()

    const {customParams} = getConfig()
    const currentPath = 'https://' + customParams.currentHost + location.pathname
    const intl = useIntl()
    const productRegistrationTitleMessage = defineMessage({
        id: 'product_registration.title',
        defaultMessage: 'Register your product',
    })

    const productRegistrationSubtitleMessage = defineMessage({
        id: 'product_registration.subtitle',
        defaultMessage: 'Register your product to unlock a world of benefits.',
    })
    const webPageSchemaMarkup = {
        '@context': 'https://schema.org/',
        '@type': 'WebPage',
        name: intl.formatMessage(productRegistrationTitleMessage),
        description: intl.formatMessage(productRegistrationSubtitleMessage),
        url: currentPath,
    }

    useEffect(() => {
        if (snParam && textParam) {
            localStorage.setItem('prepopulatedSerialNumber', snParam)
            queryParams.delete('sn')
            history.replace({search: queryParams.toString()})
        }
    }, [snParam])

    const {data: productSearchResult, isFetching} = useProductSearch(
        {
            parameters: {
                q: searchQuery,
                expand: ['represented_products', 'images', 'page_meta_tags'],
                refine: ['c_enabledForReg=true'],
                locale: locale.id,
                site: site.id,
                currency,
                c_isProductReg: true,
            },
        },
        {
            enabled: !!searchQuery,
            keepPreviousData: false,
        },
    )

    // Filter hits to only those with a represented product id
    const representedHits = productSearchResult?.hits?.filter((p) => p?.representedProduct?.id)

    // Retry logic: if no results, try different dot placements
    useEffect(() => {
        if (!isFetching && textParam) {
            const normalizedQuery = textParam.replace(/\./g, '')

            // Check if we're in a retry sequence
            const storedBaseQuery = sessionStorage.getItem('productRegistrationBaseQuery')
            const attempts = parseInt(
                sessionStorage.getItem('productRegistrationAttempts') || '0',
                10,
            )

            // If this is a new product code (first search), store it
            if (storedBaseQuery !== normalizedQuery) {
                sessionStorage.setItem('productRegistrationBaseQuery', normalizedQuery)
                sessionStorage.setItem('productRegistrationAttempts', '0')
                sessionStorage.removeItem('productRegistrationTriedWithoutDot')
                // Only proceed if new search has no results
                if (representedHits?.length > 0) {
                    return
                }
                // Fall through to retry logic below
            }

            // If we found results, stop retrying
            if (representedHits?.length > 0) {
                return
            }

            const maxPositions = Math.min(normalizedQuery.length - 1, 15)

            // Get current attempt number
            let currentAttempt = attempts
            try {
                currentAttempt = parseInt(
                    sessionStorage.getItem('productRegistrationAttempts') || '0',
                    10,
                )
            } catch {
                currentAttempt = 0
            }

            // Case 1: Still have positions to try - keep trying all positions until finding product
            if (currentAttempt < maxPositions) {
                const nextAttempt = currentAttempt + 1

                // Record this attempt
                try {
                    sessionStorage.setItem('productRegistrationAttempts', String(nextAttempt))
                } catch {
                    // sessionStorage not available
                }

                const newQuery =
                    normalizedQuery.slice(0, nextAttempt) + '.' + normalizedQuery.slice(nextAttempt)

                // Redirect to try this position
                const redirectUrl = `${
                    routingObject.PRODUCT_REGISTRATION_PATH
                }/select?text=${encodeURIComponent(newQuery)}`
                history.push(redirectUrl)
            } else if (!sessionStorage.getItem('productRegistrationTriedWithoutDot')) {
                // Case 2: Tried all positions, now try without any dot as final fallback
                sessionStorage.setItem('productRegistrationTriedWithoutDot', 'true')
                const redirectUrl = `${
                    routingObject.PRODUCT_REGISTRATION_PATH
                }/select?text=${encodeURIComponent(normalizedQuery)}`
                history.push(redirectUrl)
            }
        }
    }, [isFetching, textParam, representedHits, history])

    // Clear retry state when we get results
    useEffect(() => {
        if (representedHits?.length > 0) {
            try {
                sessionStorage.removeItem('productRegistrationAttempts')
                sessionStorage.removeItem('productRegistrationBaseQuery')
                sessionStorage.removeItem('productRegistrationTriedWithoutDot')
            } catch {
                // sessionStorage not available
            }
        }
    }, [representedHits?.length])

    const variant =
        representedHits?.length > 2
            ? 'default'
            : representedHits?.length === 2
            ? 'doubleProducts'
            : 'singleProduct'
    const productsVariant = useMultiStyleConfig('ProductRegistrationProducts', {variant})

    const handleButtonClick = (product) => {
        localStorage.setItem('selectedProduct', JSON.stringify(product))
        const productId = product?.representedProduct?.id
        navigate(`${routingObject.PRODUCT_REGISTRATION_PATH}/user-detail?code=${productId}`)
    }

    const redirectionPath = `${routingObject.PRODUCT_REGISTRATION_PATH}/search`

    const {addLayer} = useGoogleTagManager()
    useEffect(() => {
        if (representedHits && !isFetching) {
            addLayer(
                registration_event.bind(this, {
                    event: 'productRegistration',
                    eventCategory: 'afterSales',
                    eventAction: 'productRegistration',
                    eventLabel: 'select',
                    productRegistration: {
                        products: representedHits.map((product) => {
                            return {
                                id: product.representedProduct?.id, // product SKU
                                name: product.c_defaultName, // product name (English)
                                category: product.c_subCategoryDefaultName, // product category (English)
                            }
                        }),
                    },
                }),
            )
        }
    }, [representedHits, isFetching])

    return (
        <Flex {...styles.container} layerStyle={'page'}>
            <Helmet>
                <script type="application/ld+json">
                    {JSON.stringify(webPageSchemaMarkup, null, 2)}
                </script>
            </Helmet>
            <Flex {...styles.headerStepperContainer}>
                <ProductRegistrationHeader index={2} variant={'product'} />
                <ProductRegistrationStepper index={1} />
            </Flex>
            {isFetching ? (
                <LoadingSpinner />
            ) : (
                <>
                    {representedHits?.length > 0 ? (
                        <>
                            <Flex {...styles.labelSearchInnerContainer}>
                                <Text {...styles.labelSearchTitle}>
                                    <FormattedMessage
                                        id="product_registration.product_select"
                                        defaultMessage="Select your product"
                                    />
                                </Text>
                            </Flex>
                            <Stack width={'100%'}>
                                <Box width={'100%'}>
                                    <Grid {...productsVariant.productdGrid}>
                                        {representedHits?.map((product) => (
                                            <ProductRegistrationProducts
                                                key={product.productId}
                                                product={product}
                                                variant={variant}
                                                onClick={() => handleButtonClick(product)}
                                            />
                                        ))}
                                    </Grid>
                                </Box>
                            </Stack>
                        </>
                    ) : (
                        <>
                            <Flex {...styles.productNotFoundInnerContainer}>
                                <Text {...styles.labelSearchTitle}>
                                    <FormattedMessage
                                        id="product_registration.product_not_found.title"
                                        defaultMessage="No products found"
                                    />
                                </Text>
                                <Text {...styles.subtitle}>
                                    <FormattedMessage
                                        id="product_registration.product_not_found.description"
                                        defaultMessage="Please go back and try retyping the SKU or Model Number."
                                    />
                                </Text>
                                <Button
                                    {...styles.tryAgainButton}
                                    variant="primary"
                                    as={Link}
                                    href={redirectionPath}
                                >
                                    <FormattedMessage
                                        defaultMessage="Try again"
                                        id="product_registration.product_not_found.try_again_button"
                                    />
                                </Button>
                            </Flex>
                        </>
                    )}
                </>
            )}
        </Flex>
    )
}

export default ProductRegistrationSelect
